import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/reducers';
import {
    getInstallmentsByPaymentCard,
    receiveInstallmentPayment,
    setInstallmentComment
} from '../../../store/actions/installmentActions';
import { getCurrentCashCycle } from '../../../store/actions/financialActions';
import { updateEnrollmentBillingType, enviaParaAsaas } from '../../../store/actions/enrollmentActions';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Grid,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    InputAdornment,
    Chip,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { format, parseISO, isValid } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import PaymentIcon from '@mui/icons-material/Payment';
import CommentIcon from '@mui/icons-material/Comment';
import SendIcon from '@mui/icons-material/Send';

const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
        const parsed = parseISO(dateStr);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : '-';
    } catch {
        return '-';
    }
};

const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
        const parsed = parseISO(dateStr);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy HH:mm') : '';
    } catch {
        return '';
    }
};

const PAYMENT_TYPES = [
    { value: '1', label: 'Dinheiro' },
    { value: '2', label: 'Cartão Crédito' },
    { value: '3', label: 'Cartão Débito' },
    { value: '4', label: 'Cheque' },
    { value: '5', label: 'PIX' },
];

const BILLING_TYPES_ASAAS = [
    { value: 'BOLETO', label: 'BOLETO' },
    { value: 'CREDIT_CARD', label: 'CARTÃO CRÉDITO' },
    { value: 'UNDEFINED', label: 'USUÁRIO ESCOLHE' },
];

const enrollmentStatusText = (status: number): string => {
    switch (status) {
        case 2: return 'CANCELADO';
        case 3: return 'EVADIDO';
        default: return '';
    }
};

interface InstallmentFormData {
    id: number;
    Enrollment_id: number;
    NumberInEnrollment: string;
    DueDate: string;
    Amount: number;
    PayDate: string;
    Fee: number;
    Interest: number;
    Discount: number;
    Increase: number;
    Total: number;
    TotalPayment: number;
    Balance: number;
    PaymentType: string;
    Comment: string;
    CashCycle_id: number | null;
    PaymentCard: string;
    Person_id: number | null;
}

const emptyInstallment: InstallmentFormData = {
    id: 0,
    Enrollment_id: 0,
    NumberInEnrollment: '',
    DueDate: '',
    Amount: 0,
    PayDate: '',
    Fee: 0,
    Interest: 0,
    Discount: 0,
    Increase: 0,
    Total: 0,
    TotalPayment: 0,
    Balance: 0,
    PaymentType: '',
    Comment: '',
    CashCycle_id: null,
    PaymentCard: '',
    Person_id: null,
};

const ReceiveInstallments: React.FC = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const installments = useSelector((state: RootState) => state.installments.installments);
    const isFetching = useSelector((state: RootState) => state.installments.isFetching);
    const isSaving = useSelector((state: RootState) => state.installments.isSaving);
    const enrollment = useSelector((state: RootState) => state.installments.enrollment);
    const student = useSelector((state: RootState) => state.installments.student);
    const currentCashCycle = useSelector((state: RootState) => state.financial.currentCashCycle);

    const [searchValue, setSearchValue] = useState('');
    const [searched, setSearched] = useState(false);

    // Payment modal
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [installmentForm, setInstallmentForm] = useState<InstallmentFormData>(emptyInstallment);

    // Comment modal
    const [commentModalOpen, setCommentModalOpen] = useState(false);
    const [commentForm, setCommentForm] = useState({ id: 0, NumberInEnrollment: '', DueDate: '', Comment: '' });

    // ASAAS
    const [billingTypeAsaas, setBillingTypeAsaas] = useState('');

    useEffect(() => {
        // @ts-ignore
        dispatch(getCurrentCashCycle());
    }, [dispatch]);

    // Update billingType when enrollment changes
    useEffect(() => {
        if (enrollment) {
            setBillingTypeAsaas(enrollment.BillingTypeAsaas || '');
        }
    }, [enrollment]);

    const handleSearch = useCallback(() => {
        if (!searchValue.trim()) {
            enqueueSnackbar('Informe o número do carnê', { variant: 'warning' });
            return;
        }
        // @ts-ignore
        dispatch(getInstallmentsByPaymentCard(searchValue.trim()));
        setSearched(true);
    }, [dispatch, searchValue, enqueueSnackbar]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    // Open payment modal
    const openPaymentModal = (prop: any) => {
        if (!currentCashCycle || !currentCashCycle.id) {
            enqueueSnackbar('Caixa não aberto. Abra o caixa no menu Ciclos Caixa.', { variant: 'error' });
            return;
        }

        const i = prop.Interest ? parseFloat(prop.Interest) : 0;
        const f = prop.Fee ? parseFloat(prop.Fee) : 0;
        const d = prop.Discount ? parseFloat(prop.Discount) : 0;
        const increase = i + f;
        const total = (prop.Amount || 0) + increase - d;

        setInstallmentForm({
            id: prop.id,
            Enrollment_id: prop.Enrollment_id,
            NumberInEnrollment: prop.NumberInEnrollment,
            DueDate: prop.DueDate,
            Amount: prop.Amount || 0,
            PayDate: prop.PayDate || '',
            Fee: f,
            Interest: i,
            Discount: d,
            Increase: increase,
            Total: total,
            TotalPayment: prop.TotalPayment || 0,
            Balance: total - (prop.TotalPayment || 0),
            PaymentType: prop.PaymentTypes_id ? String(prop.PaymentTypes_id) : '',
            Comment: prop.Comment || '',
            CashCycle_id: currentCashCycle.id,
            PaymentCard: enrollment ? String(enrollment.PaymentCard) : searchValue,
            Person_id: student ? student.id : null,
        });
        setPaymentModalOpen(true);
    };

    // Open comment modal
    const openCommentModal = (prop: any) => {
        setCommentForm({
            id: prop.id,
            NumberInEnrollment: prop.NumberInEnrollment,
            DueDate: prop.DueDate ? prop.DueDate.substring(0, 10) : '',
            Comment: prop.Comment || '',
        });
        setCommentModalOpen(true);
    };

    // Recalculate totals
    const onFeeChange = (val: number) => {
        const increase = (installmentForm.Interest || 0) + val;
        const total = installmentForm.Amount + increase - installmentForm.Discount;
        setInstallmentForm(prev => ({
            ...prev,
            Fee: val,
            Increase: increase,
            Total: total,
            Balance: total - prev.TotalPayment,
        }));
    };

    const onInterestChange = (val: number) => {
        const increase = val + (installmentForm.Fee || 0);
        const total = installmentForm.Amount + increase - installmentForm.Discount;
        setInstallmentForm(prev => ({
            ...prev,
            Interest: val,
            Increase: increase,
            Total: total,
            Balance: total - prev.TotalPayment,
        }));
    };

    const onDiscountChange = (val: number) => {
        const total = installmentForm.Amount + installmentForm.Increase - val;
        setInstallmentForm(prev => ({
            ...prev,
            Discount: val,
            Total: total,
            Balance: total - prev.TotalPayment,
        }));
    };

    const onTotalPaymentChange = (val: number) => {
        setInstallmentForm(prev => ({
            ...prev,
            TotalPayment: val,
            Balance: prev.Total - val,
        }));
    };

    // Confirm payment
    const handleConfirmPayment = () => {
        if (!installmentForm.PaymentType || installmentForm.TotalPayment <= 0) {
            enqueueSnackbar('Selecione forma de pagamento e valor pago', { variant: 'warning' });
            return;
        }
        if (!window.confirm('Confirma recebimento pagamento?')) return;

        // @ts-ignore
        dispatch(receiveInstallmentPayment(installmentForm, installmentForm.id));
        setPaymentModalOpen(false);

        enqueueSnackbar('Pagamento registrado com sucesso', { variant: 'success' });

        // Refresh after a small delay to allow API to complete
        setTimeout(() => {
            // @ts-ignore
            dispatch(getInstallmentsByPaymentCard(searchValue.trim()));
        }, 800);
    };

    // Confirm comment
    const handleConfirmComment = () => {
        // @ts-ignore
        dispatch(setInstallmentComment({
            id: commentForm.id,
            Comment: commentForm.Comment,
            DueDate: commentForm.DueDate
        }));
        setCommentModalOpen(false);
        enqueueSnackbar('Comentário salvo', { variant: 'success' });

        setTimeout(() => {
            // @ts-ignore
            dispatch(getInstallmentsByPaymentCard(searchValue.trim()));
        }, 500);
    };

    // ASAAS: change billing type
    const handleChangeBillingType = (value: string) => {
        setBillingTypeAsaas(value);
        if (enrollment) {
            // @ts-ignore
            dispatch(updateEnrollmentBillingType(enrollment, value));
        }
    };

    // ASAAS: send to ASAAS
    const handleEnviaAsaas = () => {
        if (student && enrollment) {
            // @ts-ignore
            dispatch(enviaParaAsaas(
                student.id,
                student.idAsaas,
                enrollment.id,
                enrollment.PaymentCard
            ));
            enqueueSnackbar('Enviando para ASAAS...', { variant: 'info' });
        } else {
            enqueueSnackbar('Dados de aluno/matrícula não disponíveis', { variant: 'warning' });
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Mensalidades</Typography>
                <Box>
                    {currentCashCycle && currentCashCycle.id ? (
                        <Chip label={`Caixa Aberto - Lote #${currentCashCycle.id}`} color="success" />
                    ) : (
                        <Chip label="Caixa Fechado" color="error" />
                    )}
                </Box>
            </Box>

            {(!currentCashCycle || !currentCashCycle.id) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Caixa não aberto. Abra o caixa no menu opção Ciclos Caixa.
                </Alert>
            )}

            {/* Search + Student Info */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={2}>
                        <TextField
                            label="Carnê"
                            variant="outlined"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Button variant="contained" onClick={handleSearch} disabled={isFetching}>
                            Pesquisar
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        {searched && student && (
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Nome: {student.Name || '-'}
                                </Typography>
                                <Typography variant="body2">
                                    Carnê: {enrollment ? enrollment.PaymentCard : '-'}
                                    {enrollment && enrollment.Status > 1 && (
                                        <Chip
                                            label={enrollmentStatusText(enrollment.Status)}
                                            color="error"
                                            size="small"
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                </Typography>
                                {enrollment && enrollment.Comment && (
                                    <Typography variant="body2" color="text.secondary">
                                        {enrollment.Comment}
                                    </Typography>
                                )}
                                <Typography variant="body2">
                                    CPF: {student.IndividualIdentity || '-'}
                                    {student.Comment ? ` - ${student.Comment}` : ''}
                                    {student.idAsaas ? ` ASAAS: ${student.idAsaas}` : ''}
                                </Typography>
                            </Box>
                        )}
                        {searched && !student && !isFetching && (
                            <Typography variant="body2" color="text.secondary">
                                Nenhum aluno encontrado
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* ASAAS Section */}
            {searched && enrollment && (
                <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Tipo Pagamento Asaas</InputLabel>
                                <Select
                                    value={billingTypeAsaas}
                                    label="Tipo Pagamento Asaas"
                                    onChange={(e) => handleChangeBillingType(e.target.value as string)}
                                >
                                    {BILLING_TYPES_ASAAS.map((bt) => (
                                        <MenuItem key={bt.value} value={bt.value}>{bt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Button
                                variant="outlined"
                                startIcon={<SendIcon />}
                                onClick={handleEnviaAsaas}
                            >
                                Envia para ASAAS
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Installments Table */}
            {isFetching ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={2}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell>#</TableCell>
                                <TableCell>Vencto</TableCell>
                                <TableCell align="right">Valor</TableCell>
                                <TableCell>Data Pagto</TableCell>
                                <TableCell>Ciclo</TableCell>
                                <TableCell align="right">Multa+Juros</TableCell>
                                <TableCell align="right">Desconto</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell align="right">Pago</TableCell>
                                <TableCell align="right">Saldo</TableCell>
                                <TableCell>Comentários</TableCell>
                                <TableCell>idAsaas</TableCell>
                                <TableCell>situacaoAsaas</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {searched && installments.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={14} align="center">
                                        Nenhuma parcela encontrada para este carnê
                                    </TableCell>
                                </TableRow>
                            )}
                            {!searched && installments.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={14} align="center">
                                        Pesquise um número de carnê
                                    </TableCell>
                                </TableRow>
                            )}
                            {installments.map((prop: any, key: number) => {
                                const i = prop.Interest ? parseFloat(prop.Interest) : 0;
                                const f = prop.Fee ? parseFloat(prop.Fee) : 0;
                                const d = prop.Discount ? parseFloat(prop.Discount) : 0;
                                const increase = i + f;
                                const total = (prop.Amount || 0) - d + increase;
                                const balance = total - (prop.TotalPayment || 0);
                                const hasCashCycle = currentCashCycle && currentCashCycle.id;

                                return (
                                    <TableRow key={key} hover>
                                        <TableCell>{prop.NumberInEnrollment}</TableCell>
                                        <TableCell>{formatDate(prop.DueDate)}</TableCell>
                                        <TableCell align="right">{formatBRL(prop.Amount)}</TableCell>
                                        <TableCell>{prop.PayDate ? formatDateTime(prop.PayDate) : ''}</TableCell>
                                        <TableCell>{prop.CashCycle_id || ''}</TableCell>
                                        <TableCell align="right">{increase > 0 ? formatBRL(increase) : ''}</TableCell>
                                        <TableCell align="right">{d > 0 ? formatBRL(d) : ''}</TableCell>
                                        <TableCell align="right">{formatBRL(total)}</TableCell>
                                        <TableCell align="right">{prop.TotalPayment ? formatBRL(prop.TotalPayment) : ''}</TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="body2"
                                                color={balance > 0.01 ? 'error' : 'success.main'}
                                                fontWeight={balance > 0.01 ? 'bold' : 'normal'}
                                            >
                                                {formatBRL(Math.max(balance, 0))}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {prop.Comment || ''}
                                        </TableCell>
                                        <TableCell>{prop.idAsaas || ''}</TableCell>
                                        <TableCell>{prop.statusAsaas || ''}</TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                            {balance > 0.01 && hasCashCycle && (
                                                <Tooltip title="Registrar Pagamento">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => openPaymentModal(prop)}
                                                    >
                                                        <PaymentIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Comentário / Vencimento">
                                                <IconButton
                                                    size="small"
                                                    color="default"
                                                    onClick={() => openCommentModal(prop)}
                                                >
                                                    <CommentIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* ====== PAYMENT MODAL ====== */}
            <Dialog open={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Registro pagamento - {installmentForm.PaymentCard}
                </DialogTitle>
                <DialogContent>
                    {student && (
                        <Typography variant="h6" sx={{ mb: 2 }}>{student.Name}</Typography>
                    )}

                    {/* Row 1: #, Vencimento, Parcela */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={2}>
                            <TextField
                                label="#"
                                value={installmentForm.NumberInEnrollment}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextField
                                label="Vencto"
                                value={formatDate(installmentForm.DueDate)}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextField
                                label="Parcela"
                                value={installmentForm.Amount}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    </Grid>

                    {/* Row 2: Multa, Juros, Acréscimo */}
                    <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="flex-end">
                        <Grid item xs={4}>
                            <TextField
                                label="Multa"
                                type="number"
                                value={installmentForm.Fee}
                                size="small"
                                fullWidth
                                onChange={(e) => onFeeChange(parseFloat(e.target.value) || 0)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Juros"
                                type="number"
                                value={installmentForm.Interest}
                                size="small"
                                fullWidth
                                onChange={(e) => onInterestChange(parseFloat(e.target.value) || 0)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Acréscimo"
                                value={installmentForm.Increase.toFixed(2)}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    </Grid>

                    {/* Row 3: Desconto, Total */}
                    <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="flex-end">
                        <Grid item xs={4}>
                            <TextField
                                label="Desconto"
                                type="number"
                                value={installmentForm.Discount}
                                size="small"
                                fullWidth
                                onChange={(e) => onDiscountChange(parseFloat(e.target.value) || 0)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Total"
                                value={installmentForm.Total.toFixed(2)}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    </Grid>

                    {/* Row 4: Data Pago, Forma Pagto, Valor Pago */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={4}>
                            <TextField
                                label="Pago"
                                value={installmentForm.PayDate ? formatDateTime(installmentForm.PayDate) : ''}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Forma Pagto</InputLabel>
                                <Select
                                    value={installmentForm.PaymentType}
                                    label="Forma Pagto"
                                    onChange={(e) => setInstallmentForm(prev => ({
                                        ...prev,
                                        PaymentType: e.target.value as string
                                    }))}
                                >
                                    {PAYMENT_TYPES.map((pt) => (
                                        <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Pago"
                                type="number"
                                value={installmentForm.TotalPayment || ''}
                                size="small"
                                fullWidth
                                onChange={(e) => onTotalPaymentChange(parseFloat(e.target.value) || 0)}
                            />
                        </Grid>
                    </Grid>

                    {/* Row 5: Comentário, Saldo */}
                    <Grid container spacing={2}>
                        <Grid item xs={8}>
                            <TextField
                                label="Comentário"
                                value={installmentForm.Comment}
                                size="small"
                                fullWidth
                                onChange={(e) => setInstallmentForm(prev => ({
                                    ...prev,
                                    Comment: e.target.value
                                }))}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Saldo"
                                value={installmentForm.Balance.toFixed(2)}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true }}
                                sx={{
                                    '& .MuiInputBase-input': {
                                        color: installmentForm.Balance > 0 ? 'error.main' : 'success.main',
                                        fontWeight: 'bold'
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmPayment}
                        variant="contained"
                        color="primary"
                        disabled={
                            !installmentForm.PaymentType ||
                            installmentForm.TotalPayment <= 0 ||
                            isSaving
                        }
                    >
                        Confirmar Pagto
                    </Button>
                    <Button onClick={() => setPaymentModalOpen(false)} color="inherit">
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ====== COMMENT MODAL ====== */}
            <Dialog open={commentModalOpen} onClose={() => setCommentModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Comentário - Parcela #{commentForm.NumberInEnrollment}
                </DialogTitle>
                <DialogContent>
                    {student && (
                        <Typography variant="h6" sx={{ mb: 2 }}>{student.Name}</Typography>
                    )}

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Vencimento"
                                type="date"
                                value={commentForm.DueDate}
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => setCommentForm(prev => ({
                                    ...prev,
                                    DueDate: e.target.value
                                }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Comentário"
                                value={commentForm.Comment}
                                size="small"
                                fullWidth
                                multiline
                                rows={3}
                                onChange={(e) => setCommentForm(prev => ({
                                    ...prev,
                                    Comment: e.target.value
                                }))}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmComment}
                        variant="contained"
                        color="primary"
                        disabled={isSaving}
                    >
                        Salvar
                    </Button>
                    <Button onClick={() => setCommentModalOpen(false)} color="inherit">
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReceiveInstallments;
